/**
 * Student-style rule-based back navigation for Admin/Trainer.
 * Used only for create-session wizard steps and live room paths.
 * All other pages fall back to history navigate(-1).
 */

import type { NavigateFunction } from 'react-router-dom';

type RolePrefix = '/admin' | '/trainer';

function getRolePrefix(pathname: string): RolePrefix {
  return pathname.startsWith('/trainer') ? '/trainer' : '/admin';
}

/** True when header back should use path rules instead of history. */
export function isRuleBasedBackPath(pathname: string): boolean {
  const path = pathname.toLowerCase();
  if (path.includes('/create-session')) return true;
  if (path.includes('/live-session')) return true;
  return false;
}

/**
 * Logical previous path for create-session + live room.
 * Matches createFlowSteps order and AppRoutes.
 */
export function getBackNavigationPath(pathname: string): string {
  const prefix = getRolePrefix(pathname);
  const path = pathname.toLowerCase();

  // Live room → view sessions
  if (path.includes('/live-session')) {
    return `${prefix}/view-sessions`;
  }

  // Create wizard (most specific first)
  if (path.endsWith('/create-session/planner/question-bank')) {
    return `${prefix}/create-session/planner`;
  }
  if (path.endsWith('/create-session/schedule')) {
    return `${prefix}/create-session/planner`;
  }
  if (path.endsWith('/create-session/planner')) {
    return `${prefix}/create-session/prepare`;
  }
  if (path.endsWith('/create-session/prepare')) {
    return `${prefix}/create-session`;
  }
  if (path.endsWith('/create-session')) {
    return `${prefix}/view-sessions`;
  }

  // Fallback for any other create-session* path
  if (path.includes('/create-session')) {
    return `${prefix}/view-sessions`;
  }

  return `${prefix}/view-sessions`;
}

/**
 * Header back: rule-based + replace for create/live; history otherwise.
 */
export function navigateBack(
  navigate: NavigateFunction,
  pathname: string,
): void {
  if (isRuleBasedBackPath(pathname)) {
    navigate(getBackNavigationPath(pathname), { replace: true });
    return;
  }
  navigate(-1);
}
